import "./index.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SessionProvider, useSession } from "./lib/session/SessionContext";
import { createSupabaseAuthGateway } from "./lib/session/gateway";
import { allowedTenants, resolveMobileAccess } from "./lib/access";
import { isResetPasswordPath, parseInvitePath } from "./lib/invitePath";
import { LoadingScreen } from "./components/LoadingScreen";
import { LoginScreen } from "./screens/LoginScreen";
import { AccessDeniedScreen } from "./screens/AccessDeniedScreen";
import { TenantSelectScreen } from "./screens/TenantSelectScreen";
import { MobileAppScreen } from "./screens/MobileAppScreen";
import { ResetPasswordScreen } from "./screens/ResetPasswordScreen";
import { InviteFlow } from "./screens/InviteFlow";

const gateway = createSupabaseAuthGateway();

function Root() {
  const { status, userEmail, accessToken, tenants, tenantsLoaded, activeTenant, switchTenant, logout, error } = useSession();
  const [inviteCode, setInviteCode] = useState<string | null>(() => parseInvitePath(window.location.pathname));
  const [resetMode] = useState(() => isResetPasswordPath(window.location.pathname));
  const autoSelectRef = useRef<string | null>(null);

  const allowed = useMemo(() => allowedTenants(tenants), [tenants]);
  const activeRole = resolveMobileAccess(activeTenant?.roleSlug ?? null);

  useEffect(() => {
    if (status !== "authed" || !tenantsLoaded || activeRole !== "denied" || allowed.length !== 1) return;
    const onlyTenant = allowed[0];
    if (!onlyTenant || autoSelectRef.current === onlyTenant.tenantId) return;
    autoSelectRef.current = onlyTenant.tenantId;
    void switchTenant(onlyTenant.tenantId);
  }, [status, tenantsLoaded, activeRole, allowed, switchTenant]);

  const completeInvite = useCallback(() => {
    window.history.replaceState(null, "", "/");
    setInviteCode(null);
  }, []);

  if (resetMode) return <ResetPasswordScreen />;

  if (status === "loading") return <LoadingScreen />;

  if (inviteCode) return <InviteFlow code={inviteCode} onComplete={completeInvite} />;

  if (status === "anon") return <LoginScreen />;

  if (!tenantsLoaded && tenants.length === 0) return <LoadingScreen label="Carregando seus CTs..." />;

  if (allowed.length === 0) {
    return <AccessDeniedScreen email={userEmail} onLogout={logout} error={error} />;
  }

  if (activeRole === "denied") {
    if (allowed.length === 1 && !error) return <LoadingScreen label="Entrando no seu CT..." />;
    return <TenantSelectScreen tenants={allowed} onLogout={logout} />;
  }

  if (!activeTenant) return <TenantSelectScreen tenants={allowed} onLogout={logout} />;

  if (!accessToken) return <LoadingScreen label="Preparando seu acesso..." />;

  return (
    <MobileAppScreen
      role={activeRole}
      ctName={activeTenant.name}
      email={userEmail}
      tenantId={activeTenant.tenantId}
      token={accessToken}
    />
  );
}

export function App() {
  return (
    <SessionProvider gateway={gateway}>
      <Root />
    </SessionProvider>
  );
}

export default App;
